import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { devicesApi, deviceLabel } from '@/api/devices';
import { getLimit } from '@/lib/appConfig';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { useConfirm } from '@/components/ui/ConfirmProvider';
import { humanizeError } from '@/lib/errors';

/**
 * Устройства учеников: у кого доступ на скольких телефонах.
 *
 * ⚠️ ЭТО УЧЁТ, А НЕ ОГРАНИЧЕНИЕ. Никого не блокируем — решение владельца от
 * 31.08.2026. Номер устройства не переживает переустановку приложения, поэтому
 * отказ по такому счётчику бил бы по честным ученикам, а не по тем, кто раздал
 * пароль.
 */
export function AdminDevicesPage() {
  const { t } = useTranslation();
  const ask = useConfirm();
  const qc = useQueryClient();
  const [openStudent, setOpenStudent] = useState<string | null>(null);

  // Порог берётся из настроек приложения: захотите поднять до пяти — это
  // тумблер в «Управлении приложением», а не новая сборка.
  const threshold = getLimit('devices', 3);

  const list = useQuery({
    queryKey: ['admin', 'devices'],
    queryFn: () => devicesApi.list(),
  });

  const detail = useQuery({
    queryKey: ['admin', 'devices', openStudent],
    queryFn: () => devicesApi.detail(openStudent!),
    enabled: !!openStudent,
  });

  const reset = useMutation({
    mutationFn: (studentId: string) => devicesApi.reset(studentId),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['admin', 'devices'] });
    },
  });

  const items = list.data?.items ?? [];

  return (
    <div className="space-y-6">
      <PageHeader title={t('adminDevices.title')} subtitle={t('adminDevices.subtitle')} />

      <Card>
        <p className="mb-4 text-sm text-on-surface-variant">
          {t('adminDevices.note', { count: threshold })}
        </p>

        {list.isLoading && <p className="text-sm text-on-surface-variant">{t('common.loading')}</p>}
        {list.isError && <p className="text-sm text-error">{humanizeError(list.error)}</p>}
        {!list.isLoading && items.length === 0 && (
          <p className="text-sm text-on-surface-variant">{t('adminDevices.empty')}</p>
        )}

        <ul className="divide-y divide-outline-variant">
          {items.map((row) => {
            const many = row.device_count >= threshold;
            const open = openStudent === row.student_id;
            return (
              <li key={row.student_id} className="py-3">
                <div className="flex items-center justify-between gap-4">
                  <button
                    type="button"
                    className="min-w-0 flex-1 text-left"
                    onClick={() => setOpenStudent(open ? null : row.student_id)}
                  >
                    <span className="block truncate text-sm text-on-surface">{row.full_name}</span>
                    <span className="block truncate text-xs text-on-surface-variant">{row.email}</span>
                  </button>

                  <span
                    className={[
                      'flex-none rounded-full px-2.5 py-0.5 text-xs font-semibold tabular-nums',
                      many ? 'bg-error-container text-on-error-container' : 'bg-surface-variant text-on-surface-variant',
                    ].join(' ')}
                    title={t('adminDevices.deviceCount', { count: row.device_count })}
                  >
                    {row.device_count}
                  </span>

                  <Button
                    variant="ghost"
                    onClick={async () => {
                      // Окно приложения, а не window.confirm (§5.20, правило 5): системное окно браузера в приложении
                      // выглядит чужим и на iOS подписано адресом страницы.
                      if (await ask({ message: t('adminDevices.resetConfirm', { name: row.full_name }), danger: true })) {
                        reset.mutate(row.student_id);
                      }
                    }}
                    disabled={reset.isPending}
                  >
                    {t('adminDevices.reset')}
                  </Button>
                </div>

                {open && (
                  <ul className="mt-2 space-y-1 rounded-md bg-surface-variant/40 p-3">
                    {detail.isLoading && (
                      <li className="text-xs text-on-surface-variant">{t('common.loading')}</li>
                    )}
                    {(detail.data?.items ?? []).map((d) => (
                      <li key={d.device_id || 'unknown'} className="flex flex-wrap gap-x-4 text-xs text-on-surface-variant">
                        <span className="font-medium text-on-surface">
                          {deviceLabel(d.device_id, t('adminDevices.unknownDevice'))}
                        </span>
                        <span>{d.device_platform || '—'}</span>
                        <span>{d.ip_address || '—'}</span>
                        <span>{d.last_used_at?.slice(0, 16).replace('T', ' ') || '—'}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>

        {reset.isError && <p className="mt-3 text-sm text-error">{humanizeError(reset.error)}</p>}
      </Card>
    </div>
  );
}
